import React, { useState } from "react";
import {
  Accordion, AccordionDetails, AccordionSummary,
  Box, Button, Card, CardContent, Chip, CircularProgress,
  Divider, Grid, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from "@mui/material";
import {
  Analytics as AnalyticsIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowForward as ArrowIcon,
} from "@mui/icons-material";

const MOTIVO_LABELS = {
  sin_cuenta_plataforma: "Sin cuenta en plataforma",
  sin_telefono: "Sin teléfono registrado",
  ya_realizo_pedido: "Ya realizó pedido esta semana",
  cuenta_inactiva: "Cuenta inactiva",
};

const MOTIVO_COLORS = {
  sin_cuenta_plataforma: "#ff9800",
  sin_telefono: "#9c27b0",
  ya_realizo_pedido: "#2196f3",
  cuenta_inactiva: "#f44336",
};

const FunnelCard = ({ label, value, color, sublabel }) => (
  <Card variant="outlined" sx={{ textAlign: "center", flex: 1, minWidth: 100 }}>
    <CardContent sx={{ py: "12px !important" }}>
      <Typography variant="h4" fontWeight="bold" sx={{ color }}>
        {value ?? "—"}
      </Typography>
      <Typography variant="body2" fontWeight={500} color="text.secondary">
        {label}
      </Typography>
      {sublabel && (
        <Typography variant="caption" color="text.disabled">
          {sublabel}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const AnalisisEnvioPanel = ({ onCargar, analisis, loading, error }) => {
  const [excluidsAbierto, setExcluidosAbierto] = useState(false);

  const hayDatos = !!analisis;
  const excluidos = analisis?.excluidos ?? [];
  const motivos = analisis?.motivos_exclusion ?? {};
  const motivosConValor = Object.entries(motivos).filter(([, v]) => v > 0);

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        {/* Encabezado */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AnalyticsIcon fontSize="small" color="action" />
            <Typography variant="subtitle1" fontWeight="bold">
              Análisis de nómina de usuarios
            </Typography>
            <Typography variant="caption" color="text.secondary">
              — colaboradores Teros vs enviados reales
            </Typography>
          </Box>
          <Button
            size="small"
            variant={hayDatos ? "outlined" : "contained"}
            onClick={onCargar}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={14} /> : null}
          >
            {loading ? "Cargando..." : hayDatos ? "Actualizar" : "Cargar análisis"}
          </Button>
        </Box>

        {/* Error */}
        {error && !loading && (
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
            {error}
          </Typography>
        )}

        {/* Datos */}
        {hayDatos && !loading && (
          <>
            {/* Funnel */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
              <FunnelCard
                label="En Teros"
                value={analisis.funnel.total_teros}
                color="#1976d2"
              />
              <ArrowIcon sx={{ color: "text.disabled", flexShrink: 0 }} />
              <FunnelCard
                label="Excluidos"
                value={analisis.funnel.total_excluidos}
                color="#f44336"
                sublabel="no recibieron el mensaje"
              />
              <ArrowIcon sx={{ color: "text.disabled", flexShrink: 0 }} />
              <FunnelCard
                label="Enviados"
                value={analisis.funnel.total_enviados}
                color="#4caf50"
                sublabel="intentos de envío"
              />
            </Box>

            {/* Motivos de exclusión */}
            {motivosConValor.length > 0 && (
              <>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: "block" }}>
                  MOTIVOS DE EXCLUSIÓN
                </Typography>
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  {motivosConValor.map(([motivo, cantidad]) => (
                    <Grid item key={motivo}>
                      <Chip
                        label={`${MOTIVO_LABELS[motivo] ?? motivo}: ${cantidad}`}
                        size="small"
                        sx={{
                          color: MOTIVO_COLORS[motivo] ?? "#757575",
                          bgcolor: `${MOTIVO_COLORS[motivo] ?? "#757575"}18`,
                          fontWeight: 500,
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

            {/* Tabla de excluidos */}
            {excluidos.length > 0 && (
              <Accordion
                expanded={excluidsAbierto}
                onChange={(_, v) => setExcluidosAbierto(v)}
                disableGutters
                elevation={0}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: "8px !important" }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" fontWeight={500}>
                    Ver excluidos ({excluidos.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "grey.50" }}>
                          <TableCell><strong>Nombre (Teros)</strong></TableCell>
                          <TableCell><strong>Cédula</strong></TableCell>
                          <TableCell><strong>Sucursal</strong></TableCell>
                          <TableCell><strong>Motivo</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {excluidos.map((excluido, idx) => {
                          const color = MOTIVO_COLORS[excluido.motivo] ?? "#757575";
                          return (
                            <TableRow key={idx} hover>
                              <TableCell>{excluido.nombre_teros || "—"}</TableCell>
                              <TableCell sx={{ color: "text.secondary" }}>{excluido.cedula}</TableCell>
                              <TableCell>{excluido.sucursal || "—"}</TableCell>
                              <TableCell>
                                <Chip
                                  label={MOTIVO_LABELS[excluido.motivo] ?? excluido.motivo}
                                  size="small"
                                  sx={{ color, bgcolor: `${color}18`, fontWeight: 500 }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AnalisisEnvioPanel;
